import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Monitor,
  AlertTriangle,
  ShieldCheck,
  Brain,
  DollarSign,
  Activity,
  RefreshCw,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import MetricCard from '../components/MetricCard';
import StatusBadge from '../components/StatusBadge';
import { fetchDashboardSummary, fetchTrendingData } from '../lib/api';
import { useTenant } from '../contexts/TenantContext';
import { useTheme } from '../contexts/ThemeContext';
import { format } from 'date-fns';
import { toast } from 'sonner';

interface Summary {
  total_hosts: number;
  online_hosts: number;
  compliance_rate: number;
  alert_count_24h: number;
  hardening_score: number;
  total_ai_cost_30d: number;
  analyzer_count: number;
  recent_analysis: Array<{
    analyzer: string;
    status: string;
    summary: string;
    score?: number;
    created_at: string;
  }>;
}

export default function DashboardPage() {
  const [data, setData] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [trendData, setTrendData] = useState<any[]>([]);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const { selectedTenantId } = useTenant();
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const navigate = useNavigate();

  const [sparklines, setSparklines] = useState<Record<string, number[]>>({});
  const [trends, setTrends] = useState<Record<string, { dir: 'up' | 'down' | 'neutral'; pct: string }>>({});

  const loadData = useCallback(() => {
    const tenantParam = selectedTenantId || undefined;
    const trendMetrics = ['compliance_rate', 'hardening_score', 'alert_count'];
    const promises = [
      fetchDashboardSummary(tenantParam),
      fetchTrendingData('compliance_rate', { tenant_id: tenantParam, limit: 30 }).catch(() => []),
      ...trendMetrics.map((m) =>
        fetchTrendingData(m, { tenant_id: tenantParam, limit: 10 }).catch(() => [])
      ),
    ];
    return Promise.all(promises).then(([summary, trend, ...sparkRaw]) => {
      setData(summary);
      setTrendData(
        (trend || []).reverse().map((p: any) => ({
          ...p,
          date: p.created_at ? format(new Date(p.created_at), 'MMM d HH:mm') : '',
        }))
      );

      // Build sparklines and trend indicators
      const sparks: Record<string, number[]> = {};
      const trendInfo: Record<string, { dir: 'up' | 'down' | 'neutral'; pct: string }> = {};
      trendMetrics.forEach((metric, i) => {
        const points = (sparkRaw[i] || []).reverse().map((p: any) => p.value);
        sparks[metric] = points;
        if (points.length >= 2) {
          const prev = points[points.length - 2];
          const curr = points[points.length - 1];
          const diff = prev !== 0 ? ((curr - prev) / Math.abs(prev)) * 100 : 0;
          trendInfo[metric] = {
            dir: diff > 0.5 ? 'up' : diff < -0.5 ? 'down' : 'neutral',
            pct: `${Math.abs(diff).toFixed(1)}%`,
          };
        }
      });
      setSparklines(sparks);
      setTrends(trendInfo);
      setLastRefresh(new Date());
    });
  }, [selectedTenantId]);

  useEffect(() => {
    setLoading(true);
    loadData().catch(console.error).finally(() => setLoading(false));
  }, [loadData]);

  // Auto-refresh every 60 seconds.
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      loadData().catch(console.error);
    }, 60000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [loadData]);

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (!data) {
    return <p className="text-gray-500 dark:text-gray-400">{t('common.noData')}</p>;
  }

  const tooltipStyle = isDark
    ? { borderRadius: '8px', border: '1px solid #374151', fontSize: '12px', backgroundColor: '#1f2937', color: '#e5e7eb' }
    : { borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '12px' };

  return (
    <div className="space-y-6">
      {/* Auto-refresh indicator */}
      <div className="flex items-center justify-end gap-2 text-xs text-gray-400 dark:text-gray-500">
        <RefreshCw className="w-3 h-3" />
        {t('common.lastRefresh')}: {format(lastRefresh, 'HH:mm:ss')}
        <button
          onClick={() => { setLoading(true); loadData().then(() => toast.success(t('common.refreshed'))).catch(console.error).finally(() => setLoading(false)); }}
          className="ml-1 text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
        >
          {t('common.refresh')}
        </button>
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <MetricCard
          title={t('dashboard.totalHosts')}
          value={data.total_hosts}
          subtitle={`${data.online_hosts} ${t('dashboard.online')}`}
          icon={Monitor}
          color="blue"
          onClick={() => navigate('/hosts')}
        />
        <MetricCard
          title={t('dashboard.compliance')}
          value={`${data.compliance_rate.toFixed(1)}%`}
          icon={ShieldCheck}
          color={data.compliance_rate >= 80 ? 'green' : 'yellow'}
          trend={trends.compliance_rate?.dir}
          trendValue={trends.compliance_rate?.pct}
          sparkData={sparklines.compliance_rate}
          onClick={() => navigate('/trending')}
        />
        <MetricCard
          title={t('dashboard.alerts24h')}
          value={data.alert_count_24h}
          icon={AlertTriangle}
          color={data.alert_count_24h > 10 ? 'red' : 'green'}
          trend={trends.alert_count?.dir}
          trendValue={trends.alert_count?.pct}
          trendInverted
          sparkData={sparklines.alert_count}
          onClick={() => navigate('/alerts')}
        />
        <MetricCard
          title={t('dashboard.hardening')}
          value={`${data.hardening_score.toFixed(1)}%`}
          icon={Activity}
          color={data.hardening_score >= 70 ? 'green' : 'yellow'}
          trend={trends.hardening_score?.dir}
          trendValue={trends.hardening_score?.pct}
          sparkData={sparklines.hardening_score}
          onClick={() => navigate('/analysis')}
        />
        <MetricCard
          title={t('dashboard.aiCost30d')}
          value={`$${data.total_ai_cost_30d.toFixed(2)}`}
          icon={DollarSign}
          color="purple"
          onClick={() => navigate('/ai-results')}
        />
        <MetricCard
          title={t('dashboard.analyzers')}
          value={data.analyzer_count}
          subtitle={t('dashboard.active')}
          icon={Brain}
          color="blue"
          onClick={() => navigate('/config')}
        />
      </div>

      {/* Status summary bar */}
      <StatusSummaryBar data={data} navigate={navigate} t={t} />

      {/* Compliance gauge + Analysis breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Compliance donut */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3">{t('dashboard.complianceOverview')}</h3>
          <div className="flex items-center justify-center">
            <div className="relative w-40 h-40">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      { value: data.compliance_rate },
                      { value: 100 - data.compliance_rate },
                    ]}
                    cx="50%" cy="50%" innerRadius={50} outerRadius={65}
                    startAngle={90} endAngle={-270}
                    dataKey="value" stroke="none"
                  >
                    <Cell fill={data.compliance_rate >= 85 ? '#10b981' : data.compliance_rate >= 70 ? '#f59e0b' : '#ef4444'} />
                    <Cell fill={isDark ? '#374151' : '#f3f4f6'} />
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">{data.compliance_rate.toFixed(0)}%</span>
                <span className="text-[10px] text-gray-400 dark:text-gray-500">{t('dashboard.target')}: 85%</span>
              </div>
            </div>
          </div>
          <div className="mt-3 flex items-center justify-center gap-1.5">
            {data.compliance_rate >= 85 ? (
              <span className="text-xs text-green-600 dark:text-green-400 font-medium">{t('dashboard.aboveTarget')}</span>
            ) : (
              <span className="text-xs text-red-500 dark:text-red-400 font-medium">
                {(85 - data.compliance_rate).toFixed(1)}% {t('dashboard.belowTarget')}
              </span>
            )}
          </div>
        </div>

        {/* Analysis status breakdown */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 lg:col-span-2">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3">{t('dashboard.analyzerStatus')}</h3>
          {data.recent_analysis.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-4">{t('dashboard.noAnalysis')}</p>
          ) : (
            <AnalyzerBreakdown analyses={data.recent_analysis} isDark={isDark} />
          )}
        </div>
      </div>

      {/* Recent analysis results */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">{t('dashboard.latestAnalysis')}</h3>
        </div>
        <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
          {data.recent_analysis.length === 0 ? (
            <p className="p-5 text-gray-500 dark:text-gray-400 text-sm">{t('dashboard.noAnalysis')}</p>
          ) : (
            data.recent_analysis.map((r, i) => (
              <div key={i} className="px-5 py-3 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200 capitalize">
                    {r.analyzer.replace(/_/g, ' ')}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{r.summary}</p>
                </div>
                {r.score != null && (
                  <span className="text-sm font-mono text-gray-600 dark:text-gray-300">
                    {r.score.toFixed(1)}%
                  </span>
                )}
                <StatusBadge status={r.status} />
                <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
                  {r.created_at ? format(new Date(r.created_at), 'MMM d, HH:mm') : '-'}
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Compliance trend chart */}
      {trendData.length > 1 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-6">
          <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-4">{t('dashboard.complianceTrend')}</h3>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={trendData}>
              <defs>
                <linearGradient id="colorCompliance" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" fontSize={11} tickLine={false} stroke={isDark ? '#6b7280' : '#9ca3af'} />
              <YAxis fontSize={11} tickLine={false} domain={[0, 100]} stroke={isDark ? '#6b7280' : '#9ca3af'} />
              <Tooltip contentStyle={tooltipStyle} />
              <Area type="monotone" dataKey="value" stroke="#10b981" strokeWidth={2} fill="url(#colorCompliance)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function StatusSummaryBar({ data, navigate, t }: { data: Summary; navigate: (path: string) => void; t: (key: string, opts?: any) => string }) {
  const criticalAnalyzers = data.recent_analysis.filter((a) => a.status === 'critical').length;
  const offlineHosts = data.total_hosts - data.online_hosts;

  const items = [
    {
      show: data.alert_count_24h > 0,
      label: t('dashboard.summaryAlerts', { count: data.alert_count_24h }),
      color: data.alert_count_24h > 5 ? 'text-red-600 dark:text-red-400' : 'text-yellow-600 dark:text-yellow-400',
      dotColor: data.alert_count_24h > 5 ? 'bg-red-500' : 'bg-yellow-500',
      path: '/alerts',
    },
    {
      show: offlineHosts > 0,
      label: t('dashboard.summaryOffline', { count: offlineHosts }),
      color: 'text-orange-600 dark:text-orange-400',
      dotColor: 'bg-orange-500',
      path: '/hosts',
    },
    {
      show: criticalAnalyzers > 0,
      label: t('dashboard.summaryCritical', { count: criticalAnalyzers }),
      color: 'text-red-600 dark:text-red-400',
      dotColor: 'bg-red-500',
      path: '/analysis',
    },
    {
      show: data.compliance_rate < 85,
      label: t('dashboard.summaryCompliance', { value: data.compliance_rate.toFixed(0) }),
      color: 'text-yellow-600 dark:text-yellow-400',
      dotColor: 'bg-yellow-500',
      path: '/trending',
    },
  ].filter((i) => i.show);

  if (items.length === 0) {
    return (
      <div className="flex items-center gap-2 px-4 py-2 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
        <span className="w-2 h-2 rounded-full bg-green-500" />
        <span className="text-xs font-medium text-green-700 dark:text-green-400">{t('dashboard.allGood')}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-4 px-4 py-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 flex-wrap">
      {items.map((item, i) => (
        <button
          key={i}
          onClick={() => navigate(item.path)}
          className={`flex items-center gap-1.5 text-xs font-medium ${item.color} hover:underline`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${item.dotColor}`} />
          {item.label}
        </button>
      ))}
    </div>
  );
}

function AnalyzerBreakdown({ analyses, isDark }: { analyses: Summary['recent_analysis']; isDark: boolean }) {
  // Group by analyzer, take latest status per analyzer
  const byAnalyzer: Record<string, { status: string; score?: number }> = {};
  analyses.forEach((a) => {
    if (!byAnalyzer[a.analyzer]) {
      byAnalyzer[a.analyzer] = { status: a.status, score: a.score };
    }
  });

  const statusCounts = { ok: 0, warning: 0, critical: 0 };
  Object.values(byAnalyzer).forEach((v) => {
    if (v.status === 'ok') statusCounts.ok++;
    else if (v.status === 'warning') statusCounts.warning++;
    else statusCounts.critical++;
  });

  const barData = Object.entries(byAnalyzer).map(([name, v]) => ({
    name: name.replace(/_/g, ' '),
    score: v.score ?? 0,
    fill: v.status === 'ok' ? '#10b981' : v.status === 'warning' ? '#f59e0b' : '#ef4444',
  }));

  const tooltipStyle = isDark
    ? { borderRadius: '6px', border: '1px solid #374151', fontSize: '11px', backgroundColor: '#1f2937', color: '#e5e7eb' }
    : { borderRadius: '6px', border: '1px solid #e5e7eb', fontSize: '11px' };

  return (
    <div className="space-y-3">
      <div className="flex gap-4 text-xs">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" /> OK: {statusCounts.ok}</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-500" /> Warning: {statusCounts.warning}</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> Critical: {statusCounts.critical}</span>
      </div>
      {barData.length > 0 && (
        <ResponsiveContainer width="100%" height={Math.max(120, barData.length * 28 + 20)}>
          <BarChart data={barData} layout="vertical" margin={{ left: 0, right: 10, top: 0, bottom: 0 }}>
            <XAxis type="number" domain={[0, 100]} fontSize={10} tickLine={false} stroke={isDark ? '#6b7280' : '#9ca3af'} />
            <YAxis type="category" dataKey="name" width={110} fontSize={10} tickLine={false} stroke={isDark ? '#6b7280' : '#9ca3af'} style={{ textTransform: 'capitalize' }} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => `${v.toFixed(1)}%`} />
            <Bar dataKey="score" radius={[0, 4, 4, 0]} barSize={16}>
              {barData.map((entry, idx) => (
                <Cell key={idx} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 h-24" />
        ))}
      </div>
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 h-64" />
    </div>
  );
}
